import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import LogoutConfirmModal from "./LogoutConfirmModal";
import { useLogout } from "../hooks/useLogout";
import polarisStarLogo from "../images/media-kit/polaris-star-logo.svg";
import "../styles/rail-sidebar.css";

type SidebarAccountMenuProps = {
    footerAvatarUrl?: string | null;
    footerAvatarAlt?: string;
    /** When true, show confirm modal before logout (admin/staff/guardian). */
    confirmLogout?: boolean;
    /** When true, show Community Forum for staff/admin. */
    showSubmitIdea?: boolean;
    /** When true, show Get Support navigation item. */
    showRaiseTicket?: boolean;
    /** When true, show Help Articles navigation item. */
    showGettingStarted?: boolean;
};

export default function SidebarAccountMenu({
    footerAvatarUrl,
    footerAvatarAlt = "Account menu",
    confirmLogout = true,
    showSubmitIdea = false,
    showRaiseTicket = true,
    showGettingStarted = true,
}: SidebarAccountMenuProps) {
    const [open, setOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const { showConfirm, requestLogout, doLogout, closeConfirm } = useLogout({
        confirm: confirmLogout,
    });

    useEffect(() => {
        if (!open) return;

        const onPointerDown = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };

        document.addEventListener("mousedown", onPointerDown);
        window.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [open]);

    const handleLogout = () => {
        setOpen(false);
        requestLogout();
    };

    const handleCommunityForum = () => {
        setOpen(false);
        navigate("/community");
    };

    const handleGetSupport = () => {
        setOpen(false);
        navigate("/support-tickets");
    };

    const handleGettingStarted = () => {
        setOpen(false);
        navigate("/getting-started");
    };

    const hasAvatar = Boolean(footerAvatarUrl);
    const avatarSrc = footerAvatarUrl || polarisStarLogo;

    return (
        <div className="sidebar-account-menu" ref={menuRef} data-testid="sidebar-account-menu">
            {open && (
                <div className="sidebar-account-menu-popover" role="menu">
                    {showSubmitIdea && (
                        <button
                            type="button"
                            className="sidebar-account-menu-item"
                            role="menuitem"
                            data-testid="sidebar-account-suggest-feature"
                            onClick={handleCommunityForum}
                        >
                            <span aria-hidden="true">💡</span> Community Forum
                        </button>
                    )}
                    {showRaiseTicket && (
                        <button
                            type="button"
                            className="sidebar-account-menu-item"
                            role="menuitem"
                            data-testid="sidebar-account-get-support"
                            onClick={handleGetSupport}
                        >
                            <span aria-hidden="true">🛠️</span> Get Support
                        </button>
                    )}
                    {showGettingStarted && (
                        <button
                            type="button"
                            className="sidebar-account-menu-item"
                            role="menuitem"
                            data-testid="sidebar-account-getting-started"
                            onClick={handleGettingStarted}
                        >
                            Help Articles
                        </button>
                    )}

                    <div className="sidebar-account-menu-divider" role="separator" />
                    <div className="sidebar-account-menu-spacer" aria-hidden="true" />

                    <button
                        type="button"
                        className="sidebar-account-logout-pill"
                        role="menuitem"
                        data-testid="sidebar-account-logout"
                        onClick={handleLogout}
                    >
                        Logout
                    </button>
                </div>
            )}

            <button
                type="button"
                className="sidebar-account-menu-trigger"
                aria-label="Account menu"
                aria-haspopup="menu"
                aria-expanded={open}
                data-testid="sidebar-account-trigger"
                onClick={() => setOpen((prev) => !prev)}
            >
                <img
                    src={avatarSrc}
                    alt={footerAvatarAlt}
                    className={`sidebar-account-avatar${hasAvatar ? "" : " sidebar-account-avatar--logo"}`}
                />
            </button>

            <LogoutConfirmModal
                open={showConfirm}
                onConfirm={doLogout}
                onClose={closeConfirm}
            />
        </div>
    );
}
